/**
 * Card Grid Swatches Web Component
 *
 * Handles swatch interactions on the product *collection grid* (not the PDP).
 *
 * Behavior:
 * - Swatch click: swaps the parent card's hero <img> to show that sibling's
 *   featured image without triggering page navigation.
 * - Marks the clicked swatch as visually active (ring highlight).
 * - Writes sessionStorage.siblingSwatches_activeTab so that if the customer
 *   subsequently clicks the card title/image link, the PDP opens with the
 *   correct swatch tab already selected.
 * - Updates all card anchor links to point to the sibling product URL,
 *   preserving the ?collection= param using data-collection-handle.
 * - After optimistic image/link update, fetches the sibling's rendered card
 *   HTML to update title, price, and badges. Previous in-flight fetches are
 *   aborted via AbortController so rapid swatch clicks don't race.
 * - Truncates overflowing swatches via ResizeObserver; a "+N" pill shows the
 *   hidden count. No hardcoded breakpoints — the container width decides.
 *
 * Dependencies: none (vanilla ES2020)
 * Mounted by: snippets/card-sibling-swatches.liquid
 */

if (!customElements.get('card-grid-swatches')) {
  class CardGridSwatches extends HTMLElement {
    constructor() {
      super();
      this._onSwatchClick = this._onSwatchClick.bind(this);
      this._fetchController = null;
      this._ro = null;
    }

    connectedCallback() {
      this._cardId          = this.dataset.cardId;
      this._tabName         = this.dataset.collectionTitle || '';
      this._collectionHandle = this.dataset.collectionHandle || '';
      this._cardWrapper     = this._findCardWrapper();

      this.querySelectorAll('.sibling-swatches__swatch').forEach(swatch => {
        swatch.addEventListener('click', this._onSwatchClick);
      });

      this._ro = new ResizeObserver(() => this._applyTruncation());
      this._ro.observe(this);
    }

    disconnectedCallback() {
      this.querySelectorAll('.sibling-swatches__swatch').forEach(swatch => {
        swatch.removeEventListener('click', this._onSwatchClick);
      });
      if (this._fetchController) {
        this._fetchController.abort();
        this._fetchController = null;
      }
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
    }

    // -------------------------------------------------------------------------
    // Overflow truncation — called by ResizeObserver on every layout change.
    // Hides swatches that don't fit in the container width and shows a "+N" pill.
    // -------------------------------------------------------------------------

    _applyTruncation() {
      const swatches = [...this.querySelectorAll('.sibling-swatches__swatch')];
      const pill = this.querySelector('.card-swatches__overflow-pill');
      if (!swatches.length || !pill) return;
      if (!this.clientWidth) return;

      // Reset: reveal all swatches, hide pill
      swatches.forEach(s => s.removeAttribute('hidden'));
      pill.setAttribute('hidden', '');

      // All swatches fit — nothing to do
      if (this.scrollWidth <= this.clientWidth) return;

      const gap = parseFloat(getComputedStyle(this).gap) || 5;
      const swatchWidth = swatches[0].offsetWidth;
      if (!swatchWidth) return;

      // Measure pill at worst-case text width before committing to show it
      pill.style.visibility = 'hidden';
      pill.removeAttribute('hidden');
      pill.textContent = '+99';
      const pillWidth = pill.offsetWidth;
      pill.setAttribute('hidden', '');
      pill.style.visibility = '';

      const available = this.clientWidth - pillWidth - gap;
      const maxVisible = Math.max(1, Math.floor((available + gap) / (swatchWidth + gap)));

      if (maxVisible >= swatches.length) return;

      swatches.slice(maxVisible).forEach(s => s.setAttribute('hidden', ''));
      pill.textContent = `+${swatches.length - maxVisible}`;
      pill.removeAttribute('hidden');
    }

    // -------------------------------------------------------------------------
    // Find the parent card wrapper that contains the hero <img>
    // -------------------------------------------------------------------------

    _findCardWrapper() {
      // The card-sibling-swatches element sits inside .card__content,
      // which is a sibling of .card__inner (which contains .card__media > .media > img).
      // Walk up to the card-wrapper.
      let el = this.parentElement;
      while (el && !el.classList.contains('card-wrapper')) {
        el = el.parentElement;
      }
      return el || null;
    }

    // -------------------------------------------------------------------------
    // Swatch click — swap image, update active state, persist tab, fetch card
    // -------------------------------------------------------------------------

    _onSwatchClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const swatch     = event.currentTarget;
      const imgSrc     = swatch.dataset.imgSrc;
      const imgSrcset  = swatch.dataset.imgSrcset;
      const productUrl = swatch.dataset.productUrl;

      // 1. Swap the card hero image optimistically
      if (imgSrc && this._cardWrapper) {
        this._swapHeroImage(imgSrc, imgSrcset);
      }

      // 2. Update the card title/image links to point to the sibling product,
      //    preserving the ?collection= param via this._collectionHandle.
      if (productUrl && this._cardWrapper) {
        this._updateCardLinks(productUrl);
      }

      // 3. Persist the active tab name for PDP tab restoration
      if (this._tabName) {
        try {
          sessionStorage.setItem('siblingSwatches_activeTab', this._tabName);
        } catch (_) {}
      }

      // 4. Visually mark the clicked swatch as active
      this._setActiveSwatch(swatch);

      // 5. Fetch the sibling's rendered card to update title, price, badges.
      //    Abort any previous in-flight request to prevent race conditions.
      if (productUrl && this._cardWrapper) {
        if (this._fetchController) {
          this._fetchController.abort();
        }
        this._fetchController = new AbortController();

        const collectionParam = this._collectionHandle
          ? `collection=${encodeURIComponent(this._collectionHandle)}&`
          : '';
        const fetchUrl = `${productUrl}?${collectionParam}section_id=card-product-section`;

        fetch(fetchUrl, { signal: this._fetchController.signal })
          .then(res => res.text())
          .then(responseText => {
            this._fetchController = null;

            const parser = new DOMParser();
            const doc = parser.parseFromString(responseText, 'text/html');
            const newCardEl = doc.querySelector('.card-wrapper.product-card-wrapper');

            if (!newCardEl) return;

            // Capture old wrapper now, before any DOM moves trigger connectedCallback
            const oldWrapper = this._cardWrapper;

            // a. Preserve the optimistically swapped images to avoid re-flash
            const currentMedia = oldWrapper.querySelector('.card__media .media');
            const newMedia = newCardEl.querySelector('.card__media .media');
            if (currentMedia && newMedia) {
              newMedia.innerHTML = currentMedia.innerHTML;
            }

            // b. Preserve the sibling swatches element (fetched section can't
            //    render swatches — it lacks the section schema settings).
            const currentSwatches = oldWrapper.querySelector('card-grid-swatches');
            const newSwatchesPlaceholder = newCardEl.querySelector('card-grid-swatches');

            if (currentSwatches) {
              if (newSwatchesPlaceholder) {
                newSwatchesPlaceholder.replaceWith(currentSwatches);
              } else {
                // Insert before .card__information if no placeholder exists
                const cardContent = newCardEl.querySelector('.card__content');
                const cardInfo = newCardEl.querySelector('.card__information');
                if (cardContent && cardInfo) {
                  cardContent.insertBefore(currentSwatches, cardInfo);
                }
              }
            }

            // c. Swap the old card wrapper out of the DOM
            try {
              if (oldWrapper && oldWrapper.parentNode) {
                oldWrapper.replaceWith(newCardEl);
              }
            } catch (e) {
              console.error('[card-grid-swatches] DOM replacement error:', e);
            }

            // d. Update stored reference and fix the fetched card's links
            //    (card-product-section renders without collection context,
            //    so links lack the ?collection= param — apply it now).
            this._cardWrapper = newCardEl;
            if (productUrl) {
              this._updateCardLinks(productUrl);
            }
          })
          .catch(err => {
            if (err.name === 'AbortError') return; // expected on rapid clicks
            console.error('[card-grid-swatches] Fetch error:', err);
          });
      }
    }

    // -------------------------------------------------------------------------
    // Swap the hero image(s) inside .card__media on the parent card
    //
    // IMPORTANT: We intentionally DO NOT remove .media--hover-effect.
    // base.css contains: ".media.media--hover-effect > img + img { opacity: 0 }"
    // which is what hides the secondary (hover) image by default. Removing the
    // class would cause img+img to revert to browser-default opacity:1, making
    // it always visible on top of img0 and blocking the swapped hero image.
    //
    // Instead we update BOTH img0 (primary) and img1 (hover/secondary) to the
    // new swatch src so both hover and non-hover states show the correct colour.
    // -------------------------------------------------------------------------

    _swapHeroImage(src, srcset) {
      const media = this._cardWrapper.querySelector('.card__media .media');
      if (!media) return;

      const imgs = Array.from(media.querySelectorAll('img'));
      if (!imgs.length) return;

      imgs.forEach(img => {
        // Clone and replace to force an immediate repaint on all browsers
        // (Safari can cache srcset and not repaint the updated image otherwise)
        const newImg = img.cloneNode();
        newImg.src = src;
        if (srcset) {
          newImg.srcset = srcset;
        } else {
          newImg.removeAttribute('srcset');
        }
        img.parentNode.replaceChild(newImg, img);
      });
    }

    // -------------------------------------------------------------------------
    // Update all anchor links on the card (title, image link) to the sibling URL.
    // Collection param is read from this._collectionHandle (set at connect time
    // from data-collection-handle) — not from existing link hrefs, which may be
    // stale after a previous fetch-and-swap cycle.
    // -------------------------------------------------------------------------

    _updateCardLinks(productUrl) {
      const collectionParam = this._collectionHandle
        ? '?collection=' + encodeURIComponent(this._collectionHandle)
        : '';
      const newHref = productUrl + collectionParam;

      this._cardWrapper.querySelectorAll('a.full-unstyled-link').forEach(a => {
        a.setAttribute('href', newHref);
      });
    }

    // -------------------------------------------------------------------------
    // Toggle active swatch ring
    // -------------------------------------------------------------------------

    _setActiveSwatch(activeSwatch) {
      this.querySelectorAll('.sibling-swatches__swatch').forEach(sw => {
        sw.classList.remove('sibling-swatches__swatch--active');
        sw.removeAttribute('aria-current');
      });

      activeSwatch.classList.add('sibling-swatches__swatch--active');
      activeSwatch.setAttribute('aria-current', 'true');
    }
  }

  customElements.define('card-grid-swatches', CardGridSwatches);
}
